import React from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    InputAdornment,
    Typography,
    CircularProgress,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import Pagination from './Pagination';

const DataTable = ({
    columns,
    rows,
    loading,
    searchTerm,
    onSearchChange,
    searchPlaceholder = "Buscar...",
    noDataMessage = "No se encontraron resultados",
    actions,
    rowKey = "id",
    // Si se define, la fila entera es clickeable (puede devolver false para una fila puntual)
    onRowClick,
    isRowClickable = () => true,
    // Pagination props
    count,
    page,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
}) => {
    const hasPagination = count !== undefined && page !== undefined && onPageChange !== undefined;

    return (
        <Box>
            {/* Search Bar */}
            {onSearchChange && (
                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    <TextField
                        placeholder={searchPlaceholder}
                        value={searchTerm}
                        onChange={(e) => onSearchChange(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: '#4e7397' }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            flex: 1,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '0.5rem',
                                backgroundColor: '#ffffff',
                                '& fieldset': { borderColor: '#d0dbe7' },
                                '&:hover fieldset': { borderColor: '#800020' },
                                '&.Mui-focused fieldset': { borderColor: '#800020' },
                            },
                        }}
                    />
                </Box>
            )}

            {/* Table (desktop / tablet) */}
            <TableContainer
                component={Paper}
                sx={{
                    borderRadius: hasPagination ? '0.75rem 0.75rem 0 0' : '0.75rem',
                    border: '1px solid #d0dbe7',
                    boxShadow: 'none',
                    overflowX: 'auto',
                    display: { xs: 'none', md: 'block' },
                }}
            >
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f6f7f8' }}>
                            {columns.map((column) => (
                                <TableCell
                                    key={column.id}
                                    align={column.align || 'left'}
                                    sx={{
                                        color: '#4e7397',
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        textTransform: 'uppercase',
                                        py: 2,
                                        ...column.sx
                                    }}
                                >
                                    {column.label}
                                </TableCell>
                            ))}
                            {actions && (
                                <TableCell align="right" sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>
                                    Acciones
                                </TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={columns.length + (actions ? 1 : 0)} align="center" sx={{ py: 8 }}>
                                    <CircularProgress size={32} sx={{ color: '#800020' }} />
                                </TableCell>
                            </TableRow>
                        ) : rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={columns.length + (actions ? 1 : 0)} align="center" sx={{ py: 8 }}>
                                    <Typography sx={{ color: '#4e7397' }}>{noDataMessage}</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            rows.map((row) => (
                                <TableRow
                                    key={row[rowKey]}
                                    onClick={onRowClick && isRowClickable(row) ? () => onRowClick(row) : undefined}
                                    sx={{
                                        cursor: onRowClick && isRowClickable(row) ? 'pointer' : 'default',
                                        '&:hover': { bgcolor: '#f6f7f8' },
                                        '& .MuiTableCell-root': { borderColor: '#f0f0f0' },
                                    }}
                                >
                                    {columns.map((column) => (
                                        <TableCell key={column.id} align={column.align || 'left'} sx={column.cellSx}>
                                            {column.render ? column.render(row) : row[column.id]}
                                        </TableCell>
                                    ))}
                                    {actions && (
                                        <TableCell align="right">
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                                                {actions(row)}
                                            </Box>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Cards (mobile) */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.5 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress size={32} sx={{ color: '#800020' }} />
                    </Box>
                ) : rows.length === 0 ? (
                    <Paper sx={{ p: 4, textAlign: 'center', borderRadius: '0.75rem', border: '1px solid #d0dbe7', boxShadow: 'none' }}>
                        <Typography sx={{ color: '#4e7397' }}>{noDataMessage}</Typography>
                    </Paper>
                ) : (
                    rows.map((row) => {
                        const [firstColumn, ...restColumns] = columns;
                        return (
                            <Paper
                                key={row[rowKey]}
                                onClick={onRowClick && isRowClickable(row) ? () => onRowClick(row) : undefined}
                                sx={{ p: 2, borderRadius: '0.75rem', border: '1px solid #d0dbe7', boxShadow: 'none', cursor: onRowClick && isRowClickable(row) ? 'pointer' : 'default' }}
                            >
                                {firstColumn && (
                                    <Box sx={{ mb: restColumns.length || actions ? 1.5 : 0 }}>
                                        {firstColumn.render ? firstColumn.render(row) : (
                                            <Typography sx={{ fontWeight: 700, color: '#0e141b' }}>{row[firstColumn.id]}</Typography>
                                        )}
                                    </Box>
                                )}
                                {restColumns.map((column) => (
                                    <Box
                                        key={column.id}
                                        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, py: 0.75, borderTop: '1px solid #f0f0f0' }}
                                    >
                                        <Typography sx={{ color: '#4e7397', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', flexShrink: 0 }}>
                                            {column.label}
                                        </Typography>
                                        <Box sx={{ minWidth: 0, textAlign: 'right', color: '#0e141b', fontSize: '0.875rem' }}>
                                            {column.render ? column.render(row) : row[column.id]}
                                        </Box>
                                    </Box>
                                ))}
                                {actions && (
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mt: 1, pt: 1, borderTop: '1px solid #f0f0f0' }}>
                                        {actions(row)}
                                    </Box>
                                )}
                            </Paper>
                        );
                    })
                )}
            </Box>

            {/* Pagination Controls */}
            {hasPagination && (
                <Pagination
                    count={count}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={onPageChange}
                    onRowsPerPageChange={onRowsPerPageChange}
                />
            )}
        </Box>
    );
};

export default DataTable;
